import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Play,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Clock3,
  User,
  Tag,
  Layers3,
  FileText,
  HelpCircle,
  Video as VideoIcon,
} from 'lucide-react';
import {
  useGetAdminCourseFullQuery,
  useUpdateCourseStatusMutation,
} from '../../api/apiSlice';
import Loading from '../../components/common/Loading';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import VideoPlayer from '../../components/course/VideoPlayer';
import TheoryRenderer from '../../components/course/TheoryRenderer';

export default function AdminCoursePreview() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'kk' ? 'kk-KZ' : 'ru-RU';

  const { data, isLoading, error } = useGetAdminCourseFullQuery(courseId);
  const [updateStatus, { isLoading: isUpdating }] = useUpdateCourseStatusMutation();

  const [activeVideo, setActiveVideo] = useState(null); // {url, title}
  const [openSection, setOpenSection] = useState(0);
  // Какие уроки развёрнуты для inline-просмотра (теория/квиз).
  // По умолчанию свёрнуто — иначе при курсе с 50 уроками админка превратится
  // в простыню. Раскрывается по клику на «Посмотреть».
  const [expandedLessons, setExpandedLessons] = useState({});
  const toggleLesson = (id) =>
    setExpandedLessons((prev) => ({ ...prev, [id]: !prev[id] }));
  const [rejectionReason, setRejectionReason] = useState('');
  const [confirm, setConfirm] = useState(null); // 'approve' | 'reject'

  if (isLoading) return <Loading variant="card" label={t('common.loading')} />;
  if (error) {
    return (
      <div className="max-w-md mx-auto rounded-2xl border border-red-100 dark:border-red-900/40 bg-white dark:bg-slate-900 p-6 text-center">
        <AlertTriangle className="mx-auto h-10 w-10 text-red-500 mb-3" />
        <p className="text-slate-900 dark:text-white font-medium">
          {error?.data?.message || t('errors.generic')}
        </p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 inline-flex items-center gap-1 text-indigo-600 hover:underline"
        >
          <ArrowLeft size={14} />
          {t('common.back')}
        </button>
      </div>
    );
  }

  const course = data?.course;
  if (!course) return null;

  const fmtPrice = (p) =>
    !p ? t('common.free') : `${Number(p).toLocaleString(locale)} ${t('common.currency')}`;

  const handleApprove = async () => {
    try {
      await updateStatus({ courseId, status: 'active' }).unwrap();
      toast.success(t('admin.approved'));
      setConfirm(null);
      navigate('/dashboard/admin/course-management');
    } catch (err) {
      toast.error(err?.data?.message || t('admin.approveError'));
      setConfirm(null);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error(t('admin.rejectionReasonRequired'));
      return;
    }
    try {
      await updateStatus({ courseId, status: 'rejected', rejectionReason: rejectionReason.trim() }).unwrap();
      toast.success(t('admin.rejected'));
      setConfirm(null);
      navigate('/dashboard/admin/course-management');
    } catch (err) {
      toast.error(err?.data?.message || t('admin.rejectError'));
      setConfirm(null);
    }
  };

  const lessonCount = course.sections?.reduce((acc, s) => acc + (s.lessons?.length || 0), 0) || 0;

  return (
    <div className="space-y-6">
      <Link
        to="/dashboard/admin/course-management"
        className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-indigo-600"
      >
        <ArrowLeft size={16} />
        {t('common.back')}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: video + details */}
        <div className="lg:col-span-2 space-y-4">
          {activeVideo?.url ? (
            <VideoPlayer
              key={activeVideo.url}
              videoUrl={activeVideo.url}
              title={activeVideo.title}
              autoPlay
            />
          ) : course.previewVideoUrl ? (
            <VideoPlayer
              videoUrl={course.previewVideoUrl}
              posterUrl={course.coverImageUrl}
              title={course.title}
            />
          ) : course.coverImageUrl ? (
            <div className="rounded-2xl overflow-hidden bg-black aspect-video">
              <img src={course.coverImageUrl} alt={course.title} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="rounded-2xl bg-black aspect-video flex items-center justify-center text-slate-500">
              {t('errors.notFound')}
            </div>
          )}

          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">{course.title}</h1>

            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                <User size={14} />
                <span className="truncate">{course.instructor?.name || '—'}</span>
              </div>
              <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                <Tag size={14} />
                <span className="truncate">{course.category?.[0]?.title || t('course.noCategory')}</span>
              </div>
              <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-medium">
                {fmtPrice(course.price)}
              </div>
              <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                <Layers3 size={14} />
                <span>{course.sections?.length || 0} / {lessonCount}</span>
              </div>
            </div>

            {course.description && (
              <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300 whitespace-pre-line">
                {course.description}
              </p>
            )}
          </div>

          {/* Sections + lessons */}
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <div className="p-5 border-b border-slate-200 dark:border-slate-800">
              <h2 className="font-semibold text-slate-900 dark:text-white">
                {t('editor.curriculumTitle')}
              </h2>
            </div>
            <div className="divide-y divide-slate-200 dark:divide-slate-800">
              {(course.sections || []).map((section, idx) => {
                const isOpen = openSection === idx;
                return (
                  <div key={section._id}>
                    <button
                      type="button"
                      onClick={() => setOpenSection(isOpen ? -1 : idx)}
                      className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    >
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 shrink-0">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <span className="flex-1 font-medium text-slate-900 dark:text-white truncate">
                        {section.title}
                      </span>
                      <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0">
                        {section.lessons?.length || 0} {t('course.lessons').toLowerCase()}
                      </span>
                      {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {isOpen && (
                      <ul className="px-5 pb-4 pl-4 sm:pl-12 space-y-2">
                        {section.lessons?.map((lesson) => {
                          const lessonType = lesson.type || 'video';
                          const isExpanded = !!expandedLessons[lesson._id];
                          const TypeIcon =
                            lessonType === 'theory'
                              ? FileText
                              : lessonType === 'quiz'
                              ? HelpCircle
                              : VideoIcon;
                          const typeColor =
                            lessonType === 'theory'
                              ? 'text-blue-500'
                              : lessonType === 'quiz'
                              ? 'text-purple-500'
                              : 'text-emerald-500';
                          return (
                            <li
                              key={lesson._id}
                              className="rounded-lg border border-slate-200 dark:border-slate-800"
                            >
                              <div className="flex items-center gap-3 px-3 py-2">
                                <TypeIcon size={16} className={`shrink-0 ${typeColor}`} />
                                <span className="flex-1 text-sm text-slate-700 dark:text-slate-200 truncate">
                                  {lesson.title}
                                </span>
                                {lessonType === 'video' && lesson.videoUrl && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setActiveVideo({ url: lesson.videoUrl, title: lesson.title })
                                    }
                                    className="inline-flex items-center gap-1 rounded-md bg-indigo-600 text-white px-2.5 py-1 text-xs font-medium hover:bg-indigo-700"
                                  >
                                    <Play size={12} />
                                    Смотреть
                                  </button>
                                )}
                                {lessonType === 'video' && !lesson.videoUrl && (
                                  <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                                    <Clock3 size={12} /> нет видео
                                  </span>
                                )}
                                {(lessonType === 'theory' || lessonType === 'quiz') && (
                                  <button
                                    type="button"
                                    onClick={() => toggleLesson(lesson._id)}
                                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-2.5 py-1 text-xs font-medium"
                                  >
                                    {isExpanded ? (
                                      <ChevronUp size={12} />
                                    ) : (
                                      <ChevronDown size={12} />
                                    )}
                                    {isExpanded ? 'Скрыть' : 'Показать'}
                                  </button>
                                )}
                              </div>

                              {/* Inline-просмотр теории — модератор сразу
                                  видит markdown + LaTeX как студент. */}
                              {isExpanded && lessonType === 'theory' && (
                                <div className="border-t border-slate-200 dark:border-slate-800 p-4 bg-slate-50 dark:bg-slate-950/40">
                                  {lesson.theoryContent?.trim() ? (
                                    <TheoryRenderer content={lesson.theoryContent} />
                                  ) : (
                                    <p className="text-sm text-slate-400 italic">
                                      Текст не заполнен
                                    </p>
                                  )}
                                </div>
                              )}

                              {/* Inline-просмотр квиза — с ПРАВИЛЬНЫМИ ответами,
                                  подсвеченными зелёным. Модератор должен
                                  верифицировать корректность ответов. */}
                              {isExpanded && lessonType === 'quiz' && (
                                <div className="border-t border-slate-200 dark:border-slate-800 p-4 bg-slate-50 dark:bg-slate-950/40 space-y-3">
                                  <p className="text-xs text-purple-700 dark:text-purple-300 font-medium">
                                    Проходной балл:{' '}
                                    <b>{lesson.passingScore || 60}%</b> ·
                                    Вопросов: {lesson.questions?.length || 0}
                                  </p>
                                  {(lesson.questions || []).length === 0 && (
                                    <p className="text-sm text-amber-600 dark:text-amber-400 italic">
                                      ⚠ В квизе нет вопросов
                                    </p>
                                  )}
                                  {(lesson.questions || []).map((q, qi) => (
                                    <div
                                      key={q._id}
                                      className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3"
                                    >
                                      <p className="text-sm font-semibold text-slate-900 dark:text-white mb-2 break-words">
                                        {qi + 1}. {q.text}
                                      </p>
                                      <ul className="space-y-1">
                                        {(q.options || []).map((opt, oi) => {
                                          const correct = oi === q.correctOptionIndex;
                                          return (
                                            <li
                                              key={opt._id || oi}
                                              className={`text-xs rounded px-2 py-1 ${
                                                correct
                                                  ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-900 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-900/40 font-medium'
                                                  : 'text-slate-700 dark:text-slate-300'
                                              }`}
                                            >
                                              {correct && '✓ '}
                                              {opt.text}
                                            </li>
                                          );
                                        })}
                                      </ul>
                                      {q.explanation && (
                                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 italic break-words">
                                          {q.explanation}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right: moderation panel */}
        <aside className="lg:col-span-1 lg:sticky lg:top-24 self-start">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 space-y-4">
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white">{t('admin.moderationBadge')}</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {t('admin.moderationSubtitle')}
              </p>
            </div>

            <button
              type="button"
              onClick={() => setConfirm('approve')}
              disabled={isUpdating}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 text-white py-2.5 text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle2 size={16} />
              {t('admin.approve')}
            </button>

            <div className="pt-2 border-t border-slate-200 dark:border-slate-800">
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">
                {t('admin.rejectionReasonPlaceholder')}
              </label>
              <textarea
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder={t('admin.rejectionReasonPlaceholder')}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:ring-2 focus:ring-red-500/20 focus:border-red-500 resize-none"
              />
              <button
                type="button"
                onClick={() => setConfirm('reject')}
                disabled={isUpdating || !rejectionReason.trim()}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 text-white py-2.5 text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                <XCircle size={16} />
                {t('admin.reject')}
              </button>
            </div>
          </div>
        </aside>
      </div>

      <ConfirmDialog
        open={confirm === 'approve'}
        title={`${t('admin.approve')}?`}
        description={course.title}
        confirmLabel={t('admin.approve')}
        loading={isUpdating}
        onConfirm={handleApprove}
        onCancel={() => setConfirm(null)}
      />
      <ConfirmDialog
        open={confirm === 'reject'}
        title={`${t('admin.reject')}?`}
        description={rejectionReason}
        confirmLabel={t('admin.reject')}
        destructive
        loading={isUpdating}
        onConfirm={handleReject}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
